import { useEffect, useState } from "react";
import { PlanCreationSummary } from "@pages/create-plan/create-plan-container/plan-creation-summary";
import { LoadingScreen } from "@components/loading-screen";
import { Box } from "@mui-wrapper";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import {
  fetchNonSingleFeatures,
  fetchSingleFeatures,
} from "@store/plan-creation/plan-creation-thunks";
import {
  setCustomProducts,
  resetPlanCreationState,
  setTrialProducts,
  plansCreationStateSelector,
} from "@store/plan-creation/plan-creation-slice";
import { CreatePlanForm } from "@pages/create-plan/create-plan-container/create-plan-form";
import { useBlockNavigation } from "@router/use-block-navigation";
import { PlanCreationModeProps } from "@custom-types/base-types";
import { useAppParams } from "@router/router-helper";
import { prefillPlanCopy } from "@store/plan-creation/plan-copy-thunks";
import { useErrorHandler } from "@components/error-boundary/error-handling-context";

/** Container that holds the plan creation page and its summary */
export function CreatePlanContainer({
  creationMode,
}: PlanCreationModeProps): React.JSX.Element {
  const { setShouldBlockNavigation } = useBlockNavigation({
    alertMessage: "You have unsaved changes. Are you sure you want to leave?",
  });

  const dispatch = useAppDispatch();
  const { isFetching } = useAppSelector(plansCreationStateSelector);
  const { handleErrorWithPage } = useErrorHandler();

  // When a plan is copied, its ID is in the route
  const { planId } = useAppParams();
  const [isPrefillingCopy, setIsPrefillingCopy] = useState(Boolean(planId));

  // Reset plan creation state on first load so the container starts fresh
  useEffect(() => {
    dispatch(resetPlanCreationState());
  }, [dispatch]);

  /** Fetch the products, and prefill the plan that is copied from them */
  useEffect(() => {
    async function fetchProducts(): Promise<void> {
      await dispatch(fetchSingleFeatures());
      await dispatch(fetchNonSingleFeatures());
      dispatch(setCustomProducts());
      dispatch(setTrialProducts());

      if (!planId) {
        return;
      }

      try {
        await dispatch(prefillPlanCopy({ planId })).unwrap();
      } catch (error) {
        handleErrorWithPage({
          id: `copy-plan-${Date.now().toString()}`,
          title: "Could not copy the plan",
          error,
        });
      } finally {
        setIsPrefillingCopy(false);
      }
    }

    void fetchProducts();
  }, [dispatch, planId, handleErrorWithPage]);

  const [shouldShowSummary, setShouldShowSummary] = useState<boolean>(false);

  return (
    <LoadingScreen isFetching={isFetching || isPrefillingCopy}>
      <Box sx={{ m: "10px 110px 0px" }}>
        {shouldShowSummary ? (
          <PlanCreationSummary
            setShouldBlockNavigation={setShouldBlockNavigation}
            setShouldShowSummary={setShouldShowSummary}
          />
        ) : (
          <CreatePlanForm
            creationMode={creationMode}
            setShouldShowSummary={() => setShouldShowSummary(true)}
          />
        )}
      </Box>
    </LoadingScreen>
  );
}
